import React from 'react';
import { Grid, Typography, Divider, Button, Box } from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  formatDateUTCFormat,
  formatNumberWithCommas,
  getOrdinal
} from '../apis/Utils';

const ShipmentDetailsComponent = ({
  shipmentsDetails = [],
  groupedPickupDetails = {},
  groupedDeliveryDetails = {},
  previousRowDetails = {},
  quoteMode = ''
}) => {
  // ✅ FIX #1: Better ordinal logic - check both quoteMode AND specific TL modes
  const shouldUseOrdinal = type => {
    if (type === 'Pickup') {
      // Single pickup modes: "1 Pickup", "Single Pickup", etc.
      const pickupMode = previousRowDetails?.TLPickupMode || quoteMode;
      const isSingle =
        pickupMode?.includes('1 Pickup') || pickupMode?.includes('Single');
      return !isSingle;
    }
    // Single delivery modes: "1 Drop", "Single Drop", etc.
    const deliveryMode = previousRowDetails?.quoteMode || quoteMode;
    const isSingle =
      deliveryMode?.includes('1 Drop') || deliveryMode?.includes('Single');
    return !isSingle;
  };

  // ✅ FIX #3: Added spacing utility for better card separation
  const sectionSpacing = { mb: 3 };

  // Helper: Render item details card (weights, dimensions, etc.)
  const renderItemDetailsCard = (items, type) => (
    <Grid
      container
      sx={{
        backgroundColor: '#EEF4FC',
        borderRadius: 2,
        padding: 2 // ✅ More padding
        // mt: 1
      }}
    >
      {items.map((item, itemIndex) => (
        <React.Fragment key={`item-${itemIndex}`}>
          {itemIndex > 0 && (
            <Divider sx={{ my: 2, width: '100%', borderColor: grey[900] }} />
          )}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {item.itemType === 'Pickup' && (
                <Grid item xs={6} sm={4} md={2}>
                  <div className='rating_scree_label'>Net Weight (lbs)</div>
                  <div className='rating_scree_value'>
                    {formatNumberWithCommas(item.netWeight)}
                  </div>
                </Grid>
              )}
              <Grid item xs={6} sm={4} md={2}>
                <div className='rating_scree_label'>Gross Weight (lbs)</div>
                <div className='rating_scree_value'>
                  {formatNumberWithCommas(item.grossWeight)}
                </div>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <div className='rating_scree_label'>Handling Unit</div>
                <div className='rating_scree_value'>{item.packagingType}</div>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <div className='rating_scree_label'># of Unit</div>
                <div className='rating_scree_value'>
                  {item.units || item.pallets}
                </div>
              </Grid>
              {item.itemType === 'Pickup' && (
                <>
                  <Grid item xs={6} sm={4} md={2}>
                    <div className='rating_scree_label'>Dimensions (in)</div>
                    <div className='rating_scree_value'>
                      {item.length} x {item.width} x {item.height}
                    </div>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <div className='rating_scree_label'>Stackable?</div>
                    <div className='rating_scree_value'>
                      {item.isStackable ? 'Yes' : 'No'}
                    </div>
                  </Grid>
                </>
              )}

              {type === 'Pickup' && (
                <Grid item xs={6} sm={4} md={2}>
                  <div className='rating_scree_label'>Freight Class</div>
                  <div className='rating_scree_value'>{item.freightClass}</div>
                </Grid>
              )}
              <Grid item xs={6} sm={4} md={4}>
                <div className='rating_scree_label'>Item Description</div>
                <div
                  className='rating_scree_value'
                  sx={{ wordBreak: 'break-word' }}
                >
                  {item.itemDescription || item.itemText}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );

  // ✅ FIX #2: Improved instructions rendering with better styling
  const renderInstructionsSection = (type, instructionText) => {
    if (!instructionText) return null;

    return (
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: `1px dashed ${grey[400]}`,
          backgroundColor: grey[50],
          p: 1.5,
          borderRadius: 1
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            color: grey[700],
            fontWeight: 600,
            mb: 0.5
          }}
        >
          {type === 'Pickup'
            ? '📍 Pickup Instructions'
            : '📦 Delivery Instructions'}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: grey[800],
            pl: 1,
            borderLeft: `3px solid #2563eb`,
            lineHeight: 1.4
          }}
        >
          {instructionText}
        </Typography>
      </Box>
    );
  };

  // Helper: Render address card with schedule info
  const renderAddressCard = (items, index, type) => {
    const firstItem = items[0];
    const isMultiMode = shouldUseOrdinal(type);
    const displayType = type === 'Drop' ? 'Delivery' : type;

    return (
      <Grid
        sx={{
          backgroundColor: '#EEF4FC',
          borderRadius: 2,
          padding: 2, // ✅ More padding
          mt: 1
        }}
      >
        <div
          className='rating_scree_label'
          style={{ marginTop: 5, fontWeight: 600 }}
        >
          {isMultiMode
            ? `${getOrdinal(index + 1)} ${displayType} Address`
            : `${displayType} Address`}
        </div>
        <div className='rating_scree_value' sx={{ mt: 0.5 }}>
          {[
            firstItem?.company,
            firstItem?.address,
            firstItem?.city,
            firstItem?.state,
            firstItem?.zip,
            firstItem?.country
          ]
            .filter(Boolean)
            .join(', ') || 'No address available'}
        </div>

        <Grid container sx={{ mt: 1.5 }} spacing={2}>
          {type === 'Pickup' ? (
            <>
              <Grid item xs={6} sm={4}>
                <div className='rating_scree_label'>Pickup Date</div>
                <div className='rating_scree_value'>
                  {formatDateUTCFormat(firstItem?.pickupDate)}
                </div>
              </Grid>
              <Grid item xs={6} sm={4}>
                <div className='rating_scree_label'>Pickup Hours</div>
                <div className='rating_scree_value'>
                  {firstItem?.openTime} - {firstItem?.closeTime}
                </div>
              </Grid>
              <Grid item xs={6} sm={4}>
                <div className='rating_scree_label'>Exp Delivery Date</div>
                <div className='rating_scree_value'>
                  {formatDateUTCFormat(firstItem?.expectedDeliveryDate)}
                </div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} sm={4}>
                <div className='rating_scree_label'>Exp. Delivery Date</div>
                <div className='rating_scree_value'>
                  {formatDateUTCFormat(firstItem?.expectedDeliveryDate)}
                </div>
              </Grid>
              <Grid item xs={6} sm={4}>
                <div className='rating_scree_label'>Delivery Hours</div>
                <div className='rating_scree_value'>
                  {firstItem?.openTime} - {firstItem?.closeTime}
                </div>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  // Main: Render complete location block (label → details → address → instructions)
  const renderLocationBlock = (items, index, type, isGlobalFirst) => {
    const firstItem = items[0];
    const isMultiMode = shouldUseOrdinal(type);
    const displayType = type === 'Drop' ? 'Delivery' : type;
    const label = isMultiMode
      ? `${getOrdinal(index + 1)} ${displayType}`
      : `${displayType}`;

    // ✅ FIX #2: Get instructions for this location type
    const instructionText =
      type === 'Pickup'
        ? previousRowDetails?.pickupInstructions
        : previousRowDetails?.deliveryInstructions;

    return (
      <Grid item xs={12} key={`${type}-${index}`}>
        {/* Section Header */}
        <Grid
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Typography
            variant='h6'
            sx={{ color: '#2563eb', fontSize: 16, fontWeight: 700 }}
          >
            {label}
          </Typography>
          {firstItem?.isHazmat && type === 'Pickup' && (
            <Button
              disabled
              sx={{
                fontSize: 10,
                height: 20,
                minWidth: 40,
                fontWeight: 'bold',
                backgroundColor: 'red !important',
                color: 'white !important',
                borderRadius: '6px',
                padding: '0 8px',
                cursor: 'default',
                '&:hover': { backgroundColor: 'red !important' },
                boxShadow: 'none'
              }}
              variant='contained'
              size='small'
            >
              Hazmat
            </Button>
          )}
        </Grid>

        {/* "Shipment Details" header - only before first pickup */}
        {isGlobalFirst && type === 'Pickup' && index === 0 && (
          <Typography
            variant='subtitle1'
            sx={{
              color: '#1e40af',
              fontWeight: 700,
              my: 1.5,
              pl: 0.5,
              borderLeft: `4px solid #2563eb`
            }}
          >
            Shipment Details
          </Typography>
        )}

        {/* Item Details Card */}
        {renderItemDetailsCard(items, type)}

        {/* Address & Schedule Card */}
        {renderAddressCard(items, index, type)}

        {/* ✅ FIX #2: Instructions - only for first location of each type */}
        {index === 0 && renderInstructionsSection(type, instructionText)}
      </Grid>
    );
  };

  // Early return if no data
  if (!shipmentsDetails?.length) {
    return null;
  }

  const pickupEntries = Object.entries(groupedPickupDetails);
  const deliveryEntries = Object.entries(groupedDeliveryDetails);
  const hasPickups = pickupEntries.length > 0;
  const hasDeliveries = deliveryEntries.length > 0;

  return (
    <Grid container spacing={2}>
      {' '}
      {/* ✅ FIX #3: Added spacing between sections */}
      {/* Render all pickups in sequential order */}
      {hasPickups &&
        pickupEntries.map(([address, items], index) =>
          renderLocationBlock(items, index, 'Pickup', true)
        )}
      {/* Render all deliveries in sequential order at the end */}
      {hasDeliveries &&
        deliveryEntries.map(([address, items], index) =>
          renderLocationBlock(items, index, 'Delivery', !hasPickups)
        )}
    </Grid>
  );
};

export default ShipmentDetailsComponent;
